import { useEffect } from "react";
import { useLocation } from "wouter";
import { getRouteTreatmentBookingConfig, MEDSPARECORD_BOOKING_HUB, TREATMENT_BOOKING_URLS } from "@shared/bookingLinks";
import { captureAdAttribution, decorateBookingUrl, isBookingDestination } from "@/lib/adAttribution";

const LEGACY_GENERIC_HUBS = new Set([
  "https://medsparecord.com/book",
  "https://www.medsparecord.com/book",
]);

function isGenericBookingHub(href: string): boolean {
  try {
    const expected = new URL(MEDSPARECORD_BOOKING_HUB);
    const url = new URL(href, window.location.origin);
    return url.origin === expected.origin && url.pathname === expected.pathname && url.search === expected.search;
  } catch {
    return false;
  }
}

function isConfiguredTreatmentBookingLink(href: string, primaryHref: string): boolean {
  try {
    const url = new URL(href, window.location.origin);
    return `${url.origin}${url.pathname}` === primaryHref;
  } catch {
    return false;
  }
}

function isMedSpaRecordBookingLink(href: string): boolean {
  try {
    const url = new URL(href, window.location.origin);
    return (url.hostname === "medsparecord.com" || url.hostname === "www.medsparecord.com") && url.pathname.startsWith("/book");
  } catch {
    return false;
  }
}

// Owner-approved direct deep links. A link already pointing at one of these must
// never be rewritten to the current route's treatment, or a cross-link (e.g. a CO2
// booking link on the injectables page) would get silently retargeted.
const APPROVED_DIRECT_HREFS = [
  TREATMENT_BOOKING_URLS.xerf,
  TREATMENT_BOOKING_URLS.botoxMembership,
  TREATMENT_BOOKING_URLS.co2Laser,
];

function isApprovedDirectException(href: string): boolean {
  return APPROVED_DIRECT_HREFS.some(approved => isConfiguredTreatmentBookingLink(href, approved));
}

/**
 * Keeps legacy in-page booking anchors aligned with the owner-approved one-option
 * service-page design. Navbar and footer anchors are intentionally outside
 * #main-content and therefore remain generic booking-hub controls.
 */
export default function TreatmentBookingLinkDecorator() {
  const [location] = useLocation();

  useEffect(() => {
    const config = getRouteTreatmentBookingConfig(location);
    const normalizeGenericHubAnchors = () => {
      document.querySelectorAll<HTMLAnchorElement>("a[href]").forEach(anchor => {
        try {
          const url = new URL(anchor.href, window.location.origin);
          if (LEGACY_GENERIC_HUBS.has(`${url.origin}${url.pathname}`) ||
            (url.hostname === "medsparecord.com" && url.pathname === "/book" && url.search !== "?step=0" && !url.pathname.startsWith("/book/"))) {
            anchor.href = MEDSPARECORD_BOOKING_HUB;
          }
        } catch {
          // Ignore invalid or non-web links.
        }
      });
    };

    const normalizeTreatmentAnchors = () => {
      if (!config) return;
      const main = document.getElementById("main-content") ?? document.querySelector("main");
      if (!main) return;
      main.querySelectorAll<HTMLAnchorElement>("a[href]").forEach(anchor => {
        if (anchor.dataset.routeBookingCta || anchor.dataset.treatmentBookingOverride === config.primaryHref) return;
        const visibleLabel = anchor.textContent?.trim().toLowerCase() ?? "";
        const isGenericHub = isGenericBookingHub(anchor.href);
        const isConfiguredLink = isConfiguredTreatmentBookingLink(anchor.href, config.primaryHref);
        const isBookingLink = isMedSpaRecordBookingLink(anchor.href);
        if (!isGenericHub && !isConfiguredLink && !isBookingLink) return;

        if (isApprovedDirectException(anchor.href)) return;

        if (isGenericHub && /^browse all treatments\b/i.test(visibleLabel)) {
          anchor.remove();
          return;
        }

        if (isGenericHub || isBookingLink) anchor.href = config.primaryHref;
        anchor.dataset.treatmentBookingOverride = config.primaryHref;
        anchor.setAttribute("aria-label", `Book ${config.treatmentName}`);

        if (/^(book|schedule|learn more)\b/i.test(visibleLabel)) {
          anchor.textContent = `Book ${config.shortName}`;
        }
      });
    };

    // Runs last: both passes above rewrite hrefs, so decorating before them
    // would hand the ad click ID to a link that is about to be replaced.
    const carryAdAttribution = () => {
      const attribution = captureAdAttribution();
      if (Object.keys(attribution).length === 0) return;
      document.querySelectorAll<HTMLAnchorElement>("a[href]").forEach(anchor => {
        let url: URL;
        try {
          url = new URL(anchor.href, window.location.origin);
        } catch {
          return;
        }
        if (!isBookingDestination(url)) return;
        const decorated = decorateBookingUrl(anchor.href, attribution);
        if (decorated !== anchor.href) anchor.href = decorated;
      });
    };

    const normalize = () => {
      normalizeGenericHubAnchors();
      normalizeTreatmentAnchors();
      carryAdAttribution();
    };

    const frame = window.requestAnimationFrame(normalize);
    const observer = new MutationObserver(normalize);
    observer.observe(document.body, { childList: true, subtree: true });
    return () => {
      window.cancelAnimationFrame(frame);
      observer.disconnect();
    };
  }, [location]);

  return null;
}
